import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Badge,
  Chip,
  Button,
  Divider,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Notifications,
  MarkEmailRead,
  Delete,
  MoreVert,
  Info,
  Warning,
  CheckCircle,
  Event,
  Send,
  Add,
  Campaign,
} from '@mui/icons-material';

const notificationsData = [
  {
    id: 1,
    type: 'booking',
    title: 'New Booking Received',
    message: 'Booking #BK2027 for Deluxe Room from Emily Johnson',
    time: '5 minutes ago',
    read: false,
    priority: 'high',
  },
  {
    id: 2,
    type: 'alert',
    title: 'Maintenance Alert',
    message: 'AC Unit in Room 305 requires immediate attention',
    time: '15 minutes ago',
    read: false,
    priority: 'high',
  },
  {
    id: 3,
    type: 'info',
    title: 'Check-out Reminder',
    message: '5 guests checking out today at 11:00 AM',
    time: '1 hour ago',
    read: false,
    priority: 'medium',
  },
  {
    id: 4,
    type: 'success',
    title: 'Payment Received',
    message: 'Payment of $594 received for Booking #BK2026',
    time: '2 hours ago',
    read: true,
    priority: 'low',
  },
  {
    id: 5,
    type: 'booking',
    title: 'Booking Cancellation',
    message: 'Booking #BK2023 has been cancelled by customer',
    time: '3 hours ago',
    read: true,
    priority: 'medium',
  },
  {
    id: 6,
    type: 'info',
    title: 'New Customer Review',
    message: 'New 5-star review received from Sarah Smith',
    time: '5 hours ago',
    read: true,
    priority: 'low',
  },
];

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState(notificationsData);
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [messageForm, setMessageForm] = useState({
    recipient: 'All Customers',
    subject: '',
    message: '',
    priority: 'medium',
  });

  const tabFilters = ['All', 'Unread', 'Read'];

  const filteredNotifications = notifications.filter((notif) => {
    if (activeTab === 1) return !notif.read;
    if (activeTab === 2) return notif.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMenuClick = (event, notification) => {
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleMarkAsRead = () => {
    setNotifications(notifications.map(n => 
      n.id === selectedNotification.id ? { ...n, read: true } : n
    ));
    setAnchorEl(null);
    setSnackbar({
      open: true,
      message: 'Notification marked as read',
      severity: 'success',
    });
  };

  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    setSnackbar({
      open: true,
      message: 'All notifications marked as read',
      severity: 'success',
    });
  };

  const handleDeleteNotification = () => {
    setNotifications(notifications.filter(n => n.id !== selectedNotification.id));
    setAnchorEl(null);
    setSnackbar({
      open: true,
      message: 'Notification deleted',
      severity: 'success',
    });
  };

  const handleViewDetails = () => {
    setViewDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSendMessage = () => {
    if (!messageForm.subject || !messageForm.message) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error',
      });
      return;
    }

    const newNotification = {
      id: notifications.length + 1,
      type: 'info',
      title: messageForm.subject,
      message: `Sent to ${messageForm.recipient}: ${messageForm.message}`,
      time: 'Just now',
      read: true,
      priority: messageForm.priority,
    };

    setNotifications([newNotification, ...notifications]);
    setSnackbar({
      open: true,
      message: `Message sent successfully to ${messageForm.recipient}`,
      severity: 'success',
    });
    setSendDialogOpen(false);
    setMessageForm({
      recipient: 'All Customers',
      subject: '',
      message: '',
      priority: 'medium',
    });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'booking':
        return <Event />;
      case 'alert':
        return <Warning />;
      case 'success':
        return <CheckCircle />;
      default:
        return <Info />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'booking':
        return '#1976d2';
      case 'alert':
        return '#ed6c02';
      case 'success':
        return '#2e7d32';
      default:
        return '#9c27b0';
    }
  };

  const stats = [
    { label: 'Total', value: notifications.length, color: '#1976d2' },
    { label: 'Unread', value: unreadCount, color: '#ed6c02' },
    { label: 'High Priority', value: notifications.filter(n => n.priority === 'high').length, color: '#d32f2f' },
    { label: 'Read Today', value: notifications.filter(n => n.read && n.time.includes('hour')).length, color: '#2e7d32' },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Notifications & Messages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Stay updated with hotel activities and send announcements
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<MarkEmailRead />} onClick={handleMarkAllAsRead}>
            Mark All Read
          </Button>
          <Button variant="contained" startIcon={<Send />} onClick={() => setSendDialogOpen(true)}>
            Send Message
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color={stat.color}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            {tabFilters.map((filter) => (
              <Tab 
                key={filter} 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {filter}
                    {filter === 'Unread' && unreadCount > 0 && (
                      <Chip label={unreadCount} size="small" color="error" />
                    )}
                  </Box>
                } 
              />
            ))}
          </Tabs>
        </Box>

        {filteredNotifications.length > 0 ? (
          <List sx={{ p: 0 }}>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    bgcolor: notification.read ? 'transparent' : '#f5f9ff',
                    '&:hover': { bgcolor: '#f5f5f5' },
                  }}
                  secondaryAction={
                    <IconButton onClick={(e) => handleMenuClick(e, notification)}>
                      <MoreVert />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Badge
                      variant="dot"
                      color="error"
                      invisible={notification.read}
                    >
                      <Avatar sx={{ bgcolor: getNotificationColor(notification.type) }}>
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" fontWeight={notification.read ? 'normal' : 'bold'}>
                          {notification.title}
                        </Typography>
                        {notification.priority === 'high' && (
                          <Chip label="High Priority" size="small" color="error" />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notification.time}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box p={4} textAlign="center">
            <Notifications sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You're all caught up!
            </Typography>
          </Box>
        )}
      </Paper>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {!selectedNotification?.read && (
          <MenuItem onClick={handleMarkAsRead}>
            <MarkEmailRead fontSize="small" sx={{ mr: 1 }} />
            Mark as Read
          </MenuItem>
        )}
        <MenuItem onClick={handleViewDetails}>
          <Info fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteNotification} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* View Notification Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {selectedNotification && (
              <Avatar sx={{ bgcolor: getNotificationColor(selectedNotification.type) }}>
                {getNotificationIcon(selectedNotification.type)}
              </Avatar>
            )}
            <Typography variant="h6">Notification Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Title</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedNotification.title}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Message</Typography>
                <Typography variant="body1">{selectedNotification.message}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Time</Typography>
                <Typography variant="body1">{selectedNotification.time}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Priority</Typography>
                <Chip 
                  label={selectedNotification.priority.toUpperCase()} 
                  size="small" 
                  color={selectedNotification.priority === 'high' ? 'error' : selectedNotification.priority === 'medium' ? 'warning' : 'default'}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip 
                  label={selectedNotification.read ? 'Read' : 'Unread'} 
                  size="small" 
                  color={selectedNotification.read ? 'success' : 'warning'}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Type</Typography>
                <Typography variant="body1">{selectedNotification.type}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedNotification && !selectedNotification.read && (
            <Button variant="contained" onClick={() => { handleMarkAsRead(); setViewDialogOpen(false); }}>
              Mark as Read
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Campaign color="primary" />
            <Typography variant="h6">Send Announcement / Message</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Recipient"
                  value={messageForm.recipient}
                  onChange={(e) => setMessageForm({ ...messageForm, recipient: e.target.value })}
                  SelectProps={{ native: true }}
                >
                  <option value="All Customers">All Customers</option>
                  <option value="All Staff">All Staff</option>
                  <option value="Front Desk">Front Desk Team</option>
                  <option value="Housekeeping">Housekeeping Team</option>
                  <option value="Maintenance">Maintenance Team</option>
                  <option value="Kitchen">Kitchen Team</option>
                  <option value="Premium Members">Premium Members</option>
                  <option value="Upcoming Guests">Upcoming Guests</option>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Priority"
                  value={messageForm.priority}
                  onChange={(e) => setMessageForm({ ...messageForm, priority: e.target.value })}
                  SelectProps={{ native: true }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  required
                  placeholder="e.g., Special Holiday Offer, Maintenance Notice"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Message"
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  required
                  placeholder="Type your message here..."
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Note:</strong> This message will be sent as a notification to {messageForm.recipient} 
                    and will also be delivered via email if email notifications are enabled.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendMessage} startIcon={<Send />}>
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
